import { distance, point } from '@turf/turf';
import Polygons from 'js/algorithms/geometry/Polygons';
import { PolygonDrawingState, PolygonDrawingAction, DRAWING_MODES } from './PolygonDrawingContext';
import { Dispatch } from 'react';

// Types
type PolygonDrawingContextType = {
  state: PolygonDrawingState;
  dispatch: Dispatch<PolygonDrawingAction>;
};

// Utils
export const getLat = (latLng: any): number =>
  typeof latLng?.lat === 'function' ? latLng.lat() : latLng?.lat;

export const getLng = (latLng: any): number =>
  typeof latLng?.lng === 'function' ? latLng.lng() : latLng?.lng;

export const toGoogleLatLng = (latLng: any) =>
  new google.maps.LatLng(getLat(latLng), getLng(latLng));

export const shouldMarkerSnapToFinish = (
  context: PolygonDrawingContextType,
  map: google.maps.Map,
  marker
): boolean => {
  const { markers, drawingMode } = context.state;

  if (markers.length === 0) return false;

  const point1 = point([getLng(marker.position), getLat(marker.position)]);
  const point2 = point([getLng(markers[0].position), getLat(markers[0].position)]);

  if (
    drawingMode === DRAWING_MODES.EDIT_MERGED_FIELD ||
    drawingMode === DRAWING_MODES.EDIT_FIELD_BOUNDARIES
  ) {
    return (
      getLng(marker.position) === getLng(markers[0].position) &&
      getLat(marker.position) === getLat(markers[0].position)
    );
  }

  return (
    distance(point1, point2, { units: 'kilometers' }) < Polygons.getSnapDistance(map.getZoom())
  );
};

export const convertToArrayOfLatLngRealNumbers = (polygonPath: any[]): any[] => {
  const mappedArray = polygonPath.map((latLng) => {
    return { latitude: getLat(latLng), longitude: getLng(latLng) };
  });

  // To finish the polygon we add the first point again
  const firstPoint = polygonPath[0];
  mappedArray.push({
    latitude: getLat(firstPoint),
    longitude: getLng(firstPoint),
  });
  return mappedArray;
};

export const initializeDrawingState = (
  dispatch: Dispatch<PolygonDrawingAction>,
  startDrawing: (drawingMode: string) => void,
  initialized: boolean,
  initializeEditFieldBoundaries: () => void,
  initializeEditMergedField: () => void,
  props: {
    isDrawingNote: boolean;
    isDrawingField: boolean;
    isPlacingMarker: boolean;
    magicWand: { isSelecting: boolean };
    isDrawingFieldHole: boolean;
    isEditingFieldBoundaries: boolean;
    isEditingMergedField: boolean;
    mergedFields: { fieldName: string; fieldNumber: string };
    isSplittingField: boolean;
  }
): void => {
  if (props.isDrawingNote) {
    dispatch({
      type: 'SET_DRAWING_MODE',
      payload: DRAWING_MODES.NOTE,
    });
    startDrawing(DRAWING_MODES.NOTE);
  } else if (props.isDrawingField) {
    dispatch({
      type: 'SET_DRAWING_MODE',
      payload: DRAWING_MODES.FIELD,
    });
    startDrawing(DRAWING_MODES.FIELD);
  } else if (props.isPlacingMarker) {
    dispatch({
      type: 'SET_DRAWING_MODE',
      payload: DRAWING_MODES.MARKER,
    });
    startDrawing(DRAWING_MODES.MARKER);
  } else if (props.magicWand.isSelecting) {
    dispatch({
      type: 'SET_DRAWING_MODE',
      payload: DRAWING_MODES.SELECT,
    });
    startDrawing(DRAWING_MODES.SELECT);
  } else if (props.isDrawingFieldHole) {
    dispatch({
      type: 'SET_DRAWING_MODE',
      payload: DRAWING_MODES.FIELD_HOLE,
    });
    startDrawing(DRAWING_MODES.FIELD_HOLE);
  } else if (props.isEditingFieldBoundaries) {
    dispatch({
      type: 'SET_DRAWING_MODE',
      payload: DRAWING_MODES.EDIT_FIELD_BOUNDARIES,
    });
    startDrawing(DRAWING_MODES.EDIT_FIELD_BOUNDARIES);
    if (!initialized) {
      initializeEditFieldBoundaries();
    }
  } else if (props.isEditingMergedField) {
    dispatch({
      type: 'SET_DRAWING_MODE',
      payload: DRAWING_MODES.EDIT_MERGED_FIELD,
    });
    dispatch({
      type: 'SET_MERGE_FIELD_INPUTS',
      payload: {
        key: 'fieldName',
        value: props.mergedFields.fieldName,
      },
    });
    dispatch({
      type: 'SET_MERGE_FIELD_INPUTS',
      payload: {
        key: 'fieldNumber',
        value: props.mergedFields.fieldNumber,
      },
    });
    startDrawing(DRAWING_MODES.EDIT_MERGED_FIELD);
    if (!initialized) {
      initializeEditMergedField();
    }
  } else if (props.isSplittingField) {
    dispatch({
      type: 'SET_DRAWING_MODE',
      payload: DRAWING_MODES.SPLIT_FIELD,
    });
    startDrawing(DRAWING_MODES.SPLIT_FIELD);
  }
};
